import { createSlice, nanoid, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Single dataset surfaced inside a RAD-e card. This mirrors the shape
 * `normalizeInlineRADE` produces for each org × dataset pair.
 */
export interface RadeCardItem {
  org?: string;
  data?: Record<string, unknown> | null;
  display_schema?: Record<string, unknown> | null;
}

export type RadeLookupKind = 'maf' | 'adr';

export type RadeLookupStatus = 'pending' | 'success' | 'empty' | 'error';

/**
 * Background-fetch state for a single RAD-e lookup.
 * - `requestId` is stamped on every kickoff so consumers can guard
 *   against late responses and re-trigger with the same id.
 * - `status` lets the dropdown show a "Searching…" indicator while
 *   `pending`, surface a result row on `success`, and a "No data
 *   found" empty state on `empty` / `error`.
 * - `data` is the normalized RAD-e items, populated on resolution.
 */
export interface RadeLookupState {
  id: string;
  requestId: string;
  status: RadeLookupStatus;
  data: RadeCardItem[] | null;
}

export interface RadeState {
  /** Legacy flag: set by ConnectedUniversalSearch's `showRADE`. */
  showing: boolean;
  /** Legacy re-fetch trigger for the legacy `RADECard.tsx` effect. */
  triggerSearch: boolean;
  /** GlobalMapSearch MedicAlert / MAF id lookup channel. */
  mafLookup: RadeLookupState | null;
  /** GlobalMapSearch ADR / phone-number lookup channel. */
  adrLookup: RadeLookupState | null;
  /**
   * Which lookup the RAD-e map card is currently displaying. Set by
   * the dropdown row click; consumed by `RADECardNext` to pick which
   * lookup's `data` to render.
   */
  selectedLookup: RadeLookupKind | null;
}

const initialState: RadeState = {
  showing: false,
  triggerSearch: false,
  mafLookup: null,
  adrLookup: null,
  selectedLookup: null,
};

interface ResolvePayload {
  requestId: string;
  status: Exclude<RadeLookupStatus, 'pending'>;
  data: RadeCardItem[] | null;
}

const radeSlice = createSlice({
  name: 'radeSlice',
  initialState,
  reducers: {
    show(state) {
      state.showing = true;
      state.triggerSearch = !state.triggerSearch;
    },
    hide(state) {
      state.showing = false;
    },
    startMafLookup: {
      reducer(state, action: PayloadAction<RadeLookupState>) {
        state.mafLookup = action.payload;
      },
      prepare(payload: { id: string } | string) {
        const id = typeof payload === 'string' ? payload : payload.id;
        return {
          payload: {
            id,
            requestId: nanoid(),
            status: 'pending' as const,
            data: null,
          },
        };
      },
    },
    resolveMafLookup(state, action: PayloadAction<ResolvePayload>) {
      // Guard: only apply if the resolution matches the in-flight
      // requestId. Late responses from a previous lookup are dropped.
      if (
        state.mafLookup &&
        state.mafLookup.requestId === action.payload.requestId
      ) {
        state.mafLookup.status = action.payload.status;
        state.mafLookup.data = action.payload.data;
      }
    },
    clearMafLookup(state) {
      state.mafLookup = null;
      if (state.selectedLookup === 'maf') state.selectedLookup = null;
    },
    startAdrLookup: {
      reducer(state, action: PayloadAction<RadeLookupState>) {
        state.adrLookup = action.payload;
      },
      prepare(payload: { id: string } | string) {
        const id = typeof payload === 'string' ? payload : payload.id;
        return {
          payload: {
            id,
            requestId: nanoid(),
            status: 'pending' as const,
            data: null,
          },
        };
      },
    },
    resolveAdrLookup(state, action: PayloadAction<ResolvePayload>) {
      if (
        state.adrLookup &&
        state.adrLookup.requestId === action.payload.requestId
      ) {
        state.adrLookup.status = action.payload.status;
        state.adrLookup.data = action.payload.data;
      }
    },
    clearAdrLookup(state) {
      state.adrLookup = null;
      if (state.selectedLookup === 'adr') state.selectedLookup = null;
    },
    selectLookup(state, action: PayloadAction<RadeLookupKind>) {
      state.selectedLookup = action.payload;
    },
    clearSelectedLookup(state) {
      state.selectedLookup = null;
    },
  },
});

export const {
  show,
  hide,
  startMafLookup,
  resolveMafLookup,
  clearMafLookup,
  startAdrLookup,
  resolveAdrLookup,
  clearAdrLookup,
  selectLookup,
  clearSelectedLookup,
} = radeSlice.actions;

export default radeSlice.reducer;
